import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  type EditCommand,
  type Edl,
  type Item,
  FPS,
  framesToSec,
  itemEnd,
  locateItem,
} from '@repo/editor-core';
import type { DecoderPool, Player } from '@/engine';
import { useAssetStates, usePlayerState } from './useEngine';
import type { CursorTarget } from './choreography';
import { CaptionInspector } from './CaptionInspector';

// Horizontal zoom = pixels per frame (30fps). The default is tuned so ~30s of
// footage fits a typical timeline width at a glance (30s×30f×1.4 ≈ 1260px); the
// user can zoom in/out or auto-fit via the ruler controls.
const DEFAULT_PX_PER_FRAME = 1.4; // 42px / second
const MIN_PX_PER_FRAME = 0.3; //   9px / second (very zoomed out)
const MAX_PX_PER_FRAME = 8; //    240px / second (frame-accurate)
const TRACK_H = 46;
const CLICK_SLOP_PX = 4; // movement under this = a click, not a drag
const NUDGE = 15; // frames (0.5s)

const clampZoom = (px: number) => Math.min(MAX_PX_PER_FRAME, Math.max(MIN_PX_PER_FRAME, px));

/** A right-click menu row: a divider, or an action (optionally checked/disabled). */
type MenuEntry =
  | { sep: true }
  | { label: string; onSelect: () => void; disabled?: boolean; danger?: boolean; active?: boolean };

interface MenuState {
  x: number;
  y: number;
  itemId: string;
  /** Playhead frame captured when the menu opened — frozen so playback ticks
   *  don't re-render the menu (and "at playhead" ops stay at the click moment). */
  frame: number;
}

const KIND_LABEL: Record<string, string> = {
  video: '영상',
  broll: 'B-roll',
  overlay: 'GIF',
  caption: '자막',
  sfx: '효과음',
};
const KIND_COLOR: Record<string, string> = {
  clip: 'bg-sky-600/80 border-sky-400',
  caption: 'bg-amber-500/80 border-amber-300 dark:border-amber-700 text-black',
  transition: 'bg-fuchsia-600/80 border-fuchsia-400',
  sfx: 'bg-emerald-600/80 border-emerald-400',
  gif: 'bg-pink-600/80 border-pink-400',
};

type DragMode = 'move' | 'trim-l' | 'trim-r';
interface DragState {
  id: string;
  mode: DragMode;
  trackId: string;
  startX: number;
  origFrom: number;
  origDur: number;
  delta: number; // frames moved
  moved: boolean;
}

function blockLabel(item: Item): string {
  switch (item.type) {
    case 'clip':
      return `${item.source.sourceInSec.toFixed(1)}–${item.source.sourceOutSec.toFixed(1)}s`;
    case 'caption':
      return item.text;
    case 'transition':
      return item.kind;
    case 'sfx':
      return item.sfxId;
    case 'gif':
      return `GIF · ${(item.durationInFrames / FPS).toFixed(1)}s`;
  }
}

/** Whether an item type supports edge-trim handles (drag to set duration). */
function trimmable(type: Item['type']): boolean {
  return type === 'clip' || type === 'caption' || type === 'gif';
}

/**
 * 타임라인에서 숨기는 트랙 종류.
 *
 * 효과음은 다시 켜졌다(2026-08-13). 프리뷰와 렌더러가 **같은 파일**(에디터 앱의
 * `/sfx/*.mp3`)을 보게 만들면서 «편집 화면과 결과물이 다른 물건»이던 원인을 없앴다.
 * 숨길 것이 없으므로 비워 둔다 — 다시 뭔가를 숨기게 되면 **왜 숨기는지**를 여기 적을 것.
 */
const HIDDEN_TRACK_KINDS: ReadonlySet<string> = new Set();

export function Timeline({
  edl,
  player,
  pool,
  selectedId,
  onSelect,
  dispatch,
  onError,
  agentCursor,
  onAddCaption,
  onAddGif,
  sourceDurationSec,
}: {
  edl: Edl;
  player: Player;
  pool: DecoderPool;
  selectedId: string | null;
  onSelect: (id: string) => void;
  dispatch: (cmd: EditCommand) => string | null;
  onError: (msg: string) => void;
  agentCursor?: CursorTarget | null;
  /** Create-new-item actions (open pickers); shown in the "+" add menu. */
  onAddCaption?: () => void;
  onAddGif?: () => void;
  /** 소스 클립 길이(초). 우측 트림이 소스 밖을 읽지 않도록 클램프하는 데 쓴다. */
  sourceDurationSec?: number;
}) {
  const { currentFrame, durationInFrames } = usePlayerState(player);
  const visibleTracks = useMemo(
    () => edl.tracks.filter((t) => !HIDDEN_TRACK_KINDS.has(t.kind)),
    [edl.tracks]
  );
  const assetStates = useAssetStates(pool);
  const [drag, setDrag] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const [menu, setMenu] = useState<MenuState | null>(null);
  const closeMenu = useCallback(() => setMenu(null), []);
  const [addOpen, setAddOpen] = useState(false);

  const [pxPerFrame, setPxPerFrame] = useState(DEFAULT_PX_PER_FRAME);
  // Mirror zoom into a ref so the pointer-drag closures (bound once, on
  // pointerdown) always read the current value without re-registering.
  const pxRef = useRef(pxPerFrame);
  pxRef.current = pxPerFrame;
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Fit the whole timeline (+1s tail) into the visible scroll width.
  const fitZoom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const frames = durationInFrames + FPS;
    if (frames <= 0) return;
    setPxPerFrame(clampZoom(el.clientWidth / frames));
  }, [durationInFrames]);

  // Auto-fit once when the clip length is first known, but only to zoom OUT
  // (a short clip shouldn't get blown up past the default granularity).
  const didFitRef = useRef(false);
  useEffect(() => {
    if (didFitRef.current || durationInFrames <= 0) return;
    const el = scrollRef.current;
    if (!el) return;
    didFitRef.current = true;
    const target = clampZoom(el.clientWidth / (durationInFrames + FPS));
    if (target < DEFAULT_PX_PER_FRAME) setPxPerFrame(target);
  }, [durationInFrames]);

  const widthPx = Math.max(640, (durationInFrames + FPS) * pxPerFrame);
  const seconds = Math.ceil(framesToSec(durationInFrames)) + 1;
  // At low zoom, thin the ruler labels so they don't overlap (min ~48px apart).
  const secStep = Math.max(1, Math.ceil(48 / (FPS * pxPerFrame)));

  /** 커맨드를 적용하고 에러 메시지를 반환한다 (성공이면 null). */
  const run = useCallback(
    (cmd: EditCommand): string | null => {
      const err = dispatch(cmd);
      if (err) onError(err);
      return err;
    },
    [dispatch, onError]
  );

  // Compute the [from, dur] a drag is previewing.
  const previewFromDur = (d: DragState): [number, number] => {
    if (d.mode === 'move') return [Math.max(0, d.origFrom + d.delta), d.origDur];
    if (d.mode === 'trim-r') return [d.origFrom, Math.max(1, d.origDur + d.delta)];
    // trim-l
    const nf = Math.max(0, Math.min(d.origFrom + d.delta, d.origFrom + d.origDur - 1));
    return [nf, d.origFrom + d.origDur - nf];
  };

  /**
   * video 트랙에서 `fromFrame` 이후의 클립을 `shift` 프레임만큼 당긴다(음수면 민다).
   *
   * 갭이 남으면 익스포트에서 영상만 앞서 나간다 — 렌더러는 클립을 `clip.from`을 무시하고
   * 이어붙이는데 오디오·자막은 절대 위치를 쓰기 때문이다. 그래서 길이를 줄이는 편집은
   * 항상 뒤를 따라 당긴다. 겹침을 피하려고 당길 때는 앞에서부터, 밀 때는 뒤에서부터 옮긴다.
   */
  const rippleVideo = useCallback(
    (trackId: string, fromFrame: number, shift: number, exceptId: string) => {
      if (shift === 0) return;
      const track = edl.tracks.find((t) => t.id === trackId);
      if (!track || track.kind !== 'video') return;
      const trailing = track.items
        .filter((it) => it.id !== exceptId && it.from >= fromFrame)
        .sort((a, b) => (shift > 0 ? a.from - b.from : b.from - a.from));
      for (const it of trailing) {
        run({ op: 'move', itemId: it.id, toTrackId: trackId, at: it.from - shift });
      }
    },
    [edl, run]
  );

  const commit = useCallback(
    (d: DragState) => {
      const loc = locateItem(edl, d.id);
      if (!loc) return;
      const item = loc.item;
      const [nf, nd] = previewFromDur(d);

      if (d.mode === 'move') {
        if (nf !== d.origFrom) run({ op: 'move', itemId: d.id, toTrackId: d.trackId, at: nf });
        return;
      }
      if (d.mode === 'trim-r') {
        if (nd === d.origDur) return;
        if (item.type !== 'clip') {
          run({ op: 'trim', itemId: d.id, durationInFrames: nd });
          return;
        }
        // 소스보다 길게 늘릴 수는 없다. 넘기면 렌더러가 짧은 세그먼트를 뽑아
        // 뒷부분이 빈 mp4가 나오는데, 그건 "성공"으로 보고된다.
        const maxDur = sourceDurationSec
          ? Math.max(1, Math.round((sourceDurationSec - item.source.sourceInSec) * FPS))
          : nd;
        const clamped = Math.min(nd, maxDur);
        if (clamped === d.origDur) return;
        const err = run({
          op: 'trim',
          itemId: d.id,
          sourceOutSec: item.source.sourceInSec + clamped / FPS,
          durationInFrames: clamped,
        });
        if (err) return;
        // trim-l과 같은 이유로 뒤 클립을 당긴다 — video 트랙에 갭이 남으면 익스포트에서
        // 영상만 앞서 나간다. (trim-l만 리플하면 방향에 따라 동작이 달라져 더 혼란스럽다.)
        rippleVideo(d.trackId, d.origFrom + d.origDur, d.origDur - clamped, d.id);
        return;
      }
      // trim-l: 앞부분을 잘라낸다.
      if (nf === d.origFrom) return;
      const cutFrames = nf - d.origFrom;

      // 자막·GIF는 타임라인 위 자유 배치물이다. 시작점을 그대로 옮긴다(예전 동작).
      // video 트랙처럼 당기면 뒤 자막이 전부 따라 움직여 발화와 어긋난다.
      if (item.type !== 'clip') {
        const err = run({ op: 'move', itemId: d.id, toTrackId: d.trackId, at: nf });
        if (err) return;
        run({ op: 'trim', itemId: d.id, durationInFrames: nd });
        return;
      }

      // video 클립은 **제자리에서** 짧아지고 뒤 클립들이 잘린 만큼 당겨진다(ripple).
      // move로 클립을 오른쪽으로 밀면 앞에 갭이 생기는데, 익스포트는 영상을
      // clip.from을 무시하고 이어붙이는 반면 오디오·자막은 절대 위치를 쓰므로
      // **영상만 갭만큼 앞서 나가** 립싱크가 통째로 깨진다.
      //
      // 왼쪽으로 끌어 늘리는 경우(cutFrames < 0)는 소스 앞부분이 남아 있어야 가능하다.
      // 늘릴 수 없으면 아무것도 하지 않는다 — 절반만 적용되면 갭이 남는다.
      const maxExtendFrames = Math.floor(item.source.sourceInSec * FPS);
      const applied = Math.max(cutFrames, -maxExtendFrames);
      if (applied === 0) return;
      const nextDur = d.origDur - applied;
      if (nextDur <= 0) return;

      // 한 제스처는 전부 적용되거나 전부 취소돼야 한다: trim이 거부되면 ripple을 하지 않는다.
      const err = run({
        op: 'trim',
        itemId: d.id,
        sourceInSec: item.source.sourceInSec + applied / FPS,
        durationInFrames: nextDur,
      });
      if (err) return;

      rippleVideo(d.trackId, d.origFrom + d.origDur, applied, d.id);
    },
    [edl, run, rippleVideo, sourceDurationSec]
  );

  const onPointerMove = useCallback((e: PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const delta = Math.round((e.clientX - d.startX) / pxRef.current);
    const moved = d.moved || Math.abs(e.clientX - d.startX) > CLICK_SLOP_PX;
    const next = { ...d, delta, moved };
    dragRef.current = next;
    setDrag(next);
  }, []);

  const onPointerUp = useCallback(() => {
    document.body.style.userSelect = ''; // restore text selection after a drag
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    const d = dragRef.current;
    dragRef.current = null;
    setDrag(null);
    if (!d) return;
    if (!d.moved) {
      // treat as a click: select + seek to start
      onSelect(d.id);
      player.seek(d.origFrom);
    } else {
      commit(d);
    }
  }, [commit, onPointerMove, onSelect, player]);

  const startDrag = (e: React.PointerEvent, item: Item, trackId: string, mode: DragMode) => {
    if (e.button !== 0) return; // ignore right/middle button — let contextmenu fire
    e.stopPropagation();
    const d: DragState = {
      id: item.id,
      mode,
      trackId,
      startX: e.clientX,
      origFrom: item.from,
      origDur: item.durationInFrames,
      delta: 0,
      moved: false,
    };
    dragRef.current = d;
    setDrag(d);
    // Kill text selection for the whole drag — the listeners live on `window`,
    // so the pointer can sweep over labels/panels outside the timeline.
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  const seekFromEvent = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    player.seek(Math.round((e.clientX - rect.left) / pxRef.current));
  };

  // Scrub the playhead by dragging the ruler (the seconds row). pointerdown
  // seeks at once (a plain click still works); dragging keeps seeking. Listeners
  // live on `window` so the pointer can sweep past the thin ruler strip. seek()
  // clamps to [0, duration-1] internally, so no bounds math is needed here.
  const rulerRef = useRef<HTMLDivElement | null>(null);
  const scrubTo = useCallback(
    (clientX: number) => {
      const el = rulerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      player.seek(Math.round((clientX - rect.left) / pxRef.current));
    },
    [player]
  );
  const onScrubMove = useCallback((e: PointerEvent) => scrubTo(e.clientX), [scrubTo]);
  const onScrubUp = useCallback(() => {
    document.body.style.userSelect = '';
    window.removeEventListener('pointermove', onScrubMove);
    window.removeEventListener('pointerup', onScrubUp);
  }, [onScrubMove]);
  const startScrub = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return; // left-button drag only
      e.preventDefault();
      player.pause(); // scrubbing = hunting for a frame; stop playback fighting the seek
      scrubTo(e.clientX);
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', onScrubMove);
      window.addEventListener('pointerup', onScrubUp);
    },
    [player, scrubTo, onScrubMove, onScrubUp]
  );
  // Drop any in-flight scrub listeners if the timeline unmounts mid-drag.
  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', onScrubMove);
      window.removeEventListener('pointerup', onScrubUp);
      document.body.style.userSelect = '';
    };
  }, [onScrubMove, onScrubUp]);

  const openMenu = (e: React.MouseEvent, itemId: string) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(itemId);
    setMenu({ x: e.clientX, y: e.clientY, itemId, frame: currentFrame });
  };

  // Type-appropriate right-click actions for the located item. `atFrame` is the
  // frozen playhead from menu-open (see MenuState.frame). Stable identity via
  // useCallback so the memoized menu content doesn't rebuild every playback tick.
  const buildEntries = useCallback(
    (loc: NonNullable<ReturnType<typeof locateItem>>, atFrame: number): MenuEntry[] => {
      const { item, track } = loc;
      const end = itemEnd(item);
      const atPlayhead = atFrame > item.from && atFrame < end;
      const fire = (cmd: EditCommand) => {
        run(cmd);
        closeMenu();
      };
      const entries: MenuEntry[] = [];

      if (item.type === 'clip') {
        entries.push({
          label: '✂ 재생헤드에서 분할',
          disabled: !atPlayhead,
          onSelect: () => fire({ op: 'split', itemId: item.id, atFrame }),
        });
        entries.push({
          label: '⊣ 끝을 재생헤드로',
          disabled: !atPlayhead,
          onSelect: () =>
            fire({ op: 'trim', itemId: item.id, durationInFrames: atFrame - item.from }),
        });
        const nextClip =
          track.items
            .filter((i) => i.type === 'clip')
            .sort((a, b) => a.from - b.from)
            .find((c) => c.from > item.from) ?? null;
        if (nextClip) {
          for (const kind of ['fade', 'whip', 'zoom'] as const) {
            entries.push({
              label: `⤫ 트랜지션: ${kind}`,
              onSelect: () =>
                fire({
                  op: 'addTransition',
                  afterItemId: item.id,
                  beforeItemId: nextClip.id,
                  kind,
                  durationInFrames: 30,
                }),
            });
          }
        }
        entries.push({
          label: '⧉ B-roll(PIP) 복제',
          onSelect: () =>
            fire({
              op: 'addClip',
              trackId: 'broll-1',
              source: { ...item.source },
              at: item.from,
              ...(item.crop ? { crop: item.crop } : {}),
              rect: { x: 0.6, y: 0.05, w: 0.35, h: 0.2 },
            }),
        });
        if (track.id === 'video-1') {
          entries.push({ sep: true });
          /*
           * 🔴 **자동으로 들어간 줌을 지울 수단.**
           *
           * 펀치 줌은 프로젝트를 열 때부터 이미 들어가 있다(기본 켬). 그런데 AI 가 «터지는
           * 지점»을 잘못 짚으면 아무것도 아닌 데서 화면이 당겨지고, **지울 방법이 없으면
           * 자동은 장점이 아니라 리스크가 된다** — 검수 원문: "틀렸을 때 내가 그 줌 하나를
           * 지울 수 있나? 못 지우면 자동은 장점이 아니라 리스크다."
           *
           * 크롭이 걸린 클립에서만 뜬다(안 걸린 클립엔 지울 게 없다).
           */
          if (item.crop) {
            /*
             * 🔴 **«줌 넣기 전»으로 돌린다 — «아무것도 안 한 상태»가 아니다.**
             *
             * 전체 화면으로 초기화하면 사람이 잡아 둔 프레이밍(채팅창 빼기 등)까지 같이
             * 풀린다. 검수 원문: "틀린 줌 하나 지우려다 **더 눈에 띄는 걸 만든다.**
             * 앞뒤 컷이랑 화면 크기가 달라 보이고, 잘라놨던 채팅창이 그 1.5초 동안만
             * 도로 나온다."
             *
             * 기준은 **같은 소스 클립들 중 가장 넓은 크롭** — 줌을 판정할 때와 같은 원리다
             * (`editor-core/src/zoom.ts`). 이웃 컷이 0.6 으로 잡혀 있으면 0.6 으로 돌아간다.
             */
            const siblings = edl.tracks
              .filter((t) => t.kind === 'video')
              .flatMap((t) => t.items)
              .filter(
                (i): i is typeof item =>
                  i.type === 'clip' &&
                  item.type === 'clip' &&
                  i.source.assetId === item.source.assetId
              );
            const widest = siblings.reduce<{ x: number; y: number; w: number; h: number } | null>(
              (best, i) => {
                const c = i.type === 'clip' ? i.crop : undefined;
                if (!c) return { x: 0, y: 0, w: 1, h: 1 };
                return !best || c.w > best.w ? c : best;
              },
              null
            ) ?? { x: 0, y: 0, w: 1, h: 1 };
            // 이미 가장 넓은 크롭이면 되돌릴 게 없다.
            if (item.type === 'clip' && item.crop.w < widest.w - 0.001) {
              entries.push({
                label: '⤢ 확대 되돌리기',
                onSelect: () => fire({ op: 'setCrop', itemId: item.id, crop: { ...widest } }),
              });
            }
          }
          for (const [mode, label] of [
            ['blur', '블러 배경'],
            ['cover', '꽉 채움'],
            ['contain', '레터박스'],
          ] as const) {
            entries.push({
              label: `화면 맞춤: ${label}`,
              active: (item.fit ?? 'blur') === mode,
              onSelect: () => fire({ op: 'setFit', itemId: item.id, fit: mode }),
            });
          }
        }
      }

      // Common actions — every item can be nudged and deleted.
      entries.push({ sep: true });
      entries.push({
        label: '◀ 0.5초',
        disabled: item.from <= 0,
        onSelect: () =>
          fire({
            op: 'move',
            itemId: item.id,
            toTrackId: track.id,
            at: Math.max(0, item.from - NUDGE),
          }),
      });
      entries.push({
        label: '0.5초 ▶',
        onSelect: () =>
          fire({ op: 'move', itemId: item.id, toTrackId: track.id, at: item.from + NUDGE }),
      });
      entries.push({ sep: true });
      entries.push({
        label: '🗑 삭제',
        danger: true,
        onSelect: () => fire({ op: 'remove', itemId: item.id }),
      });
      return entries;
    },
    // edl.tracks 를 읽어 «이웃 컷의 크롭»을 찾는다(확대 되돌리기) — 의존성에 넣지 않으면
    // 컷이 바뀐 뒤에도 옛 값으로 되돌린다.
    [run, closeMenu, edl.tracks]
  );

  // Resolve + build the menu content once per (menu, edl) — NOT per playback
  // frame — so the open menu's inputs don't re-render on every tick.
  const menuContent = useMemo(() => {
    if (!menu) return null;
    const loc = locateItem(edl, menu.itemId);
    if (!loc) return null;
    const header =
      loc.item.type === 'caption' ? (
        <CaptionInspector caption={loc.item} dispatch={dispatch} onError={onError} />
      ) : null;
    return { header, entries: buildEntries(loc, menu.frame) };
  }, [menu, edl, dispatch, onError, buildEntries]);

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border bg-card">
      {/* Zoom controls */}
      <div className="flex items-center gap-2 border-b px-2 py-1 text-xs text-muted-foreground">
        {(onAddCaption || onAddGif) && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setAddOpen((v) => !v)}
              className="rounded border px-1.5 leading-5 text-foreground hover:bg-secondary"
              title="추가 (자막 · GIF)"
              aria-haspopup="menu"
              aria-expanded={addOpen}
            >
              ＋
            </button>
            {addOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setAddOpen(false)} />
                <div className="absolute left-0 top-full z-40 mt-1 min-w-[8.5rem] overflow-hidden rounded-md border bg-card py-1 text-foreground shadow-lg">
                  {onAddCaption && (
                    <button
                      type="button"
                      onClick={() => {
                        setAddOpen(false);
                        onAddCaption();
                      }}
                      className="block w-full px-3 py-1.5 text-left text-xs hover:bg-secondary"
                    >
                      + 자막
                    </button>
                  )}
                  {onAddGif && (
                    <button
                      type="button"
                      onClick={() => {
                        setAddOpen(false);
                        onAddGif();
                      }}
                      className="block w-full px-3 py-1.5 text-left text-xs hover:bg-secondary"
                    >
                      + GIF
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        <span className="uppercase">확대</span>
        <button
          type="button"
          onClick={() => setPxPerFrame((p) => clampZoom(p / 1.5))}
          className="rounded border px-1.5 leading-5 hover:bg-secondary"
          title="축소"
        >
          −
        </button>
        <input
          type="range"
          min={MIN_PX_PER_FRAME}
          max={MAX_PX_PER_FRAME}
          step={0.05}
          value={pxPerFrame}
          onChange={(e) => setPxPerFrame(clampZoom(Number(e.target.value)))}
          className="h-1 w-32 cursor-pointer accent-primary"
          aria-label="타임라인 확대"
        />
        <button
          type="button"
          onClick={() => setPxPerFrame((p) => clampZoom(p * 1.5))}
          className="rounded border px-1.5 leading-5 hover:bg-secondary"
          title="확대"
        >
          +
        </button>
        <button
          type="button"
          onClick={fitZoom}
          className="rounded border px-1.5 leading-5 hover:bg-secondary"
          title="전체를 화면에 맞춤"
        >
          맞춤
        </button>
      </div>
      <div ref={scrollRef} className="overflow-x-auto">
        <div className="relative select-none" style={{ width: widthPx }}>
          {/* Ruler — drag anywhere on the seconds row to scrub the playhead. */}
          <div
            ref={rulerRef}
            className="relative h-6 cursor-ew-resize select-none border-b bg-secondary/40"
            onPointerDown={startScrub}
          >
            {Array.from({ length: seconds }, (_, s) => s)
              .filter((s) => s % secStep === 0)
              .map((s) => (
                <div
                  key={s}
                  className="absolute top-0 h-full border-l border-border/60 pl-1 text-[10px] text-muted-foreground"
                  style={{ left: s * FPS * pxPerFrame }}
                >
                  {s}s
                </div>
              ))}
          </div>

          {/* Tracks — sfx는 기능 비활성화 상태라 숨긴다 (HIDDEN_TRACK_KINDS) */}
          {visibleTracks.map((track) => (
            <div
              key={track.id}
              className="relative border-b"
              style={{ height: TRACK_H }}
              onClick={seekFromEvent}
            >
              <span className="pointer-events-none absolute left-1 top-1 z-10 text-[10px] uppercase text-muted-foreground">
                {KIND_LABEL[track.kind] ?? track.kind}
              </span>
              {track.items.map((item) => {
                const dragging = drag?.id === item.id;
                const [from, dur] =
                  dragging && drag ? previewFromDur(drag) : [item.from, itemEnd(item) - item.from];
                const left = from * pxPerFrame;
                const width = Math.max(8, dur * pxPerFrame);
                const selected = item.id === selectedId;
                const canTrim = trimmable(item.type);
                // Media load status for clip items (video/broll). Missing = not
                // yet warmed up → treat as loading (preload kicks in on EDL load).
                const status = item.type === 'clip' ? assetStates[item.source.assetId] : undefined;
                const loadState = item.type === 'clip' ? (status?.state ?? 'loading') : null;
                const loadPct = Math.round((status?.progress ?? 0) * 100);
                return (
                  <div
                    key={item.id}
                    title={blockLabel(item)}
                    onPointerDown={(e) => startDrag(e, item, track.id, 'move')}
                    onContextMenu={(e) => openMenu(e, item.id)}
                    className={`absolute top-1.5 flex h-[34px] cursor-grab items-center overflow-hidden rounded border text-[11px] active:cursor-grabbing ${
                      KIND_COLOR[item.type]
                    } ${selected ? 'ring-2 ring-foreground' : ''} ${dragging ? 'opacity-80' : ''}`}
                    style={{ left, width }}
                  >
                    {canTrim && (
                      <div
                        onPointerDown={(e) => startDrag(e, item, track.id, 'trim-l')}
                        className="absolute left-0 top-0 h-full w-2 cursor-ew-resize bg-black/30"
                      />
                    )}
                    <span className="pointer-events-none w-full truncate px-2.5">
                      {blockLabel(item)}
                    </span>
                    {loadState && loadState !== 'ready' && (
                      <div
                        className={`pointer-events-none absolute inset-0 z-[1] flex items-center justify-center gap-1 ${
                          loadState === 'error'
                            ? 'bg-red-950/70 text-red-100'
                            : 'bg-black/45 text-white/90'
                        }`}
                      >
                        {loadState === 'error' ? (
                          <span className="text-[10px] font-semibold">⚠ 로드 실패</span>
                        ) : (
                          <>
                            <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                            {width > 44 && (
                              <span className="text-[10px] tabular-nums">
                                {loadPct > 0 ? `${loadPct}%` : '로딩…'}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    )}
                    {canTrim && (
                      <div
                        onPointerDown={(e) => startDrag(e, item, track.id, 'trim-r')}
                        className="absolute right-0 top-0 h-full w-2 cursor-ew-resize bg-black/30"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          {/* Playhead */}
          <div
            className="pointer-events-none absolute top-0 z-20 w-0.5 bg-red-500"
            style={{ left: currentFrame * pxPerFrame, height: 24 + visibleTracks.length * TRACK_H }}
          />

          {/* Agent cursor (pure presentation — choreographs agent edits) */}
          {agentCursor &&
            (() => {
              const ti = visibleTracks.findIndex((t) => t.kind === agentCursor.trackKind);
              const top = 24 + (ti < 0 ? 0 : ti) * TRACK_H + TRACK_H / 2;
              return (
                <div
                  className="pointer-events-none absolute z-30 flex items-center gap-1"
                  style={{
                    left: agentCursor.frame * pxPerFrame,
                    top,
                    transition: 'left 0.35s ease, top 0.35s ease',
                  }}
                >
                  <span className="text-lg drop-shadow">👆</span>
                  <span className="whitespace-nowrap rounded bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
                    {agentCursor.label}
                  </span>
                </div>
              );
            })()}
        </div>
      </div>

      {menu && menuContent && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          header={menuContent.header}
          entries={menuContent.entries}
          onClose={closeMenu}
        />
      )}
    </div>
  );
}

/**
 * Cursor-positioned right-click menu, portaled to the body and viewport-clamped.
 * Memoized so playback frame ticks in the parent don't re-render it (its props —
 * x/y/entries/header/onClose — are held stable across ticks by the caller).
 */
const ContextMenu = memo(function ContextMenu({
  x,
  y,
  entries,
  header,
  onClose,
}: {
  x: number;
  y: number;
  entries: MenuEntry[];
  /** Optional rich editor (e.g. caption fields) rendered above the action list. */
  header?: React.ReactNode;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState({ left: x, top: y });

  // Keep the menu on-screen once its size is known.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      left: Math.max(8, Math.min(x, window.innerWidth - r.width - 8)),
      top: Math.max(8, Math.min(y, window.innerHeight - r.height - 8)),
    });
  }, [x, y]);

  // Dismiss on outside click, Escape, or window blur.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    window.addEventListener('blur', onClose);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('blur', onClose);
    };
  }, [onClose]);

  // A leading divider would double up with the header's own divider — drop it.
  const rows = header && entries[0] && 'sep' in entries[0] ? entries.slice(1) : entries;

  return createPortal(
    <div
      ref={ref}
      className={`fixed z-50 select-none rounded-md border bg-card p-1 text-xs shadow-xl ${
        header ? 'w-64' : 'min-w-44'
      }`}
      style={{ left: pos.left, top: pos.top }}
      onContextMenu={(e) => {
        // Allow the native menu (paste, etc.) inside form fields; block elsewhere.
        const tag = (e.target as HTMLElement).tagName;
        if (!/^(INPUT|TEXTAREA|SELECT)$/.test(tag)) e.preventDefault();
      }}
    >
      {header && (
        <>
          <div className="px-1.5 py-1">{header}</div>
          {rows.length > 0 && <div className="my-1 border-t border-border/60" />}
        </>
      )}
      {rows.map((en, i) =>
        'sep' in en ? (
          <div key={i} className="my-1 border-t border-border/60" />
        ) : (
          <button
            key={i}
            type="button"
            disabled={en.disabled}
            onClick={en.onSelect}
            className={`flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left disabled:opacity-40 ${
              en.danger ? 'text-destructive hover:bg-destructive/10' : 'hover:bg-secondary'
            }`}
          >
            <span className={`w-2.5 text-[10px] ${en.active ? '' : 'opacity-0'}`}>●</span>
            <span className="flex-1">{en.label}</span>
          </button>
        )
      )}
    </div>,
    document.body
  );
});
